import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import React, { useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import {
  AirportAutoCompleteField,
  DatepickerField,
  ReactSelectField,
} from "../forms/inputGroup/helpers";
import { PaxPicker } from "../MainSearchForm/widgets";
import {
  getFieldsOptions,
  validateFlightSearch,
} from "../MainSearchForm/flights/helpers";
import { DateTime } from "luxon";
import { SearchButton } from "../common/Button";
import PaxPickerModal from "../MainSearchForm/modals/PaxPickerModal";

function createPayload({ form }) {
  const payload = {
    adt: form.adults,
    chd: form.children,
    ages: form.children_ages,
    rTrp: form.roundTrip,
    bags: form.baggage_only,
    stops: form.stops,
    cls: form.flight_class,
    orgn_ia: form.origin.origin_meta[0].iata,
    orgn_nm: form.origin.origin_meta[0].label,
    dest_ia: form.destination.destination_meta[0].iata,
    dest_nm: form.destination.destination_meta[0].label,
    out: form.outbound_departure,
    in: form.inbound_departure,
  };
  return payload;
}

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepFlightSearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepFlightSearchForm", {}),
  }),
  searchModal: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: [jssVariables.double_gap, jssVariables.normal_gap],
    borderRadius: jssVariables.border_radius,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    padding: [0, jssVariables.normal_gap],
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),
  flightsSearchForm: (props) => ({
    padding: [0, jssVariables.normal_gap],
    border: `${props.borderSize} solid ${props.baseColor}`,
    borderRadius: props.borderRadius,
    display: "grid",
    gridGap: jssVariables.half_gap,
    background: props.background,
    ..._.get(props, "customStyles.flightsSearchForm", {}),
  }),
  fieldsContainer: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.fieldsContainer", {}),
  }),
  basicFields: (props) => ({
    gridGap: jssVariables.half_gap,
    display: "grid",
    ..._.get(props, "customStyles.basicFields", {}),
  }),
  extraFields: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.extraFields", {}),
  }),
  destMobile: {},
  destDesktop: {
    display: "none",
  },
  col2Top: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.col2Top", {}),
  }),
  col2Bottom: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.col2Bottom", {}),
  }),
  stopsCompMobile: (props) => ({
    ..._.get(props, "customStyles.stopsCompMobile", {}),
  }),
  stopsCompDesktop: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.stopsCompDesktop", {}),
  }),
  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      padding: jssVariables.double_gap,
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    header: (props) => ({
      padding: 0,
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    flightsSearchForm: (props) => ({
      gridTemplateColumns: "1fr max-content",
      alignItems: "end",
      padding: 0,
      ..._.get(props, "customStyles.flightsSearchForm.smallscreen", {}),
    }),
    fieldsContainer: (props) => ({
      ..._.get(props, "customStyles.fieldsContainer.smallscreen", {}),
    }),
    basicFields: (props) => ({
      display: "grid",
      gridTemplateColumns: "9rem 1fr 1fr",
      ..._.get(props, "customStyles.basicFields.smallscreen", {}),
    }),
    extraFields: (props) => ({
      gridTemplateColumns: "9rem 13rem 14rem 1fr",
      ..._.get(props, "customStyles.extraFields.smallscreen", {}),
    }),
    destMobile: {
      display: "none",
    },
    destDesktop: {
      display: "block",
    },
    col2Top: (props) => ({
      gridTemplateColumns: "1fr",
      ..._.get(props, "customStyles.col2Top.smallscreen", {}),
    }),
    col2Bottom: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.col2Bottom.smallscreen", {}),
    }),
    stopsCompMobile: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.stopsCompMobile.smallscreen", {}),
    }),
    stopsCompDesktop: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.stopsCompDesktop.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      margin: "unset",
      paddingBottom: "1px",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    flightsSearchForm: (props) => ({
      ..._.get(props, "customStyles.flightsSearchForm.bigscreen", {}),
    }),
  },
});
const DeepFlightSearchForm = () => {
  const [pax, setPax] = useState({});
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { setShowFlFormModal } = useContext(GlobalModalsContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepFlightSearchForm",
  });

  const classes = styles({
    customStyles,
  });

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  const search_form = useSelector((state) => state.FlightSearchForm);
  const { outbound_departure, isRoundTrip } = search_form;
  const [roundTrip, setRoundTrip] = useState(isRoundTrip);

  async function onNewSearch({ form }) {
    const encoded_data = window.btoa(JSON.stringify(createPayload({ form })));
    window.open(`/deep-flight-search/${encoded_data}`, "_blank");
    setShowFlFormModal(false);
  }

  return (
    <div className={classes.DeepFlightSearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowFlFormModal(false)}
          />
        </div>
        <Formik
          validateOnBlur={false}
          validateOnMount={false}
          initialValues={{
            origin: {
              origin_meta: _.get(search_form, "origin_meta", []),
            },
            destination: {
              destination_meta: _.get(search_form, "destination_meta", []),
            },
            adults: _.get(search_form, "adults", 0),
            children: _.get(search_form, "children", 0),
            children_ages: _.get(search_form, "children_ages", []),
            datesArray: [outbound_departure, null],
            roundTrip: {
              value: false,
              label: t("oneWay"),
            },
            baggage_only: {
              value: false,
              label: t("noBaggagePreference"),
            },
            stops: {
              value: "AllFlight",
              label: t("anyFlights"),
            },
            flight_class: {
              value: "All",
              label: t("noClassPreference"),
            },
          }}
          validate={async (values) => {
            if (values.roundTrip !== roundTrip) {
              setRoundTrip(values.roundTrip.value);
            }
          }}
          onSubmit={async (values) => {
            var form = {
              origin: values.origin,
              destination: values.destination,
              outbound_departure: values.datesArray[0],
              isRoundTrip: values.roundTrip.value,
              baggage_only: values.baggage_only.value,
              stops: values.stops.value,
              flight_class: values.flight_class.value,
              adults: pax.adults ? pax.adults : search_form.adults,
              children: pax.children ? pax.children : search_form.children,
              children_ages: pax.children_ages
                ? pax.children_ages
                : search_form.children_ages,
            };
            if (form.isRoundTrip) {
              form = {
                ...form,
                inbound_departure: values.datesArray[1],
              };
            }
            if (validateFlightSearch(form)) {
              onNewSearch({ form });
            }
          }}>
          <Form className={classes.flightsSearchForm}>
            <div className={classes.fieldsContainer}>
              <div className={classes.basicFields}>
                <div className={classes.col2Top}>
                  <ReactSelectField
                    name="roundTrip"
                    options={getFieldsOptions({ name: "roundTrip", t })}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                  <div className={classes.stopsCompMobile}>
                    <ReactSelectField
                      name="stops"
                      options={getFieldsOptions({ name: "stops", t })}
                      customStyles={_.get(customStyles, "inputGroupField", {})}
                    />
                  </div>
                </div>
                <AirportAutoCompleteField
                  name="origin"
                  placeholder={`${t("originAirport")}`}
                  withErrors={false}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
                <span className={classes.destDesktop}>
                  <AirportAutoCompleteField
                    name="destination"
                    placeholder={`${t("destination")} ${t("airport")}`}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </span>
                <span className={classes.destMobile}>
                  <AirportAutoCompleteField
                    name="destination"
                    placeholder={`${t("destination")} ${t("airport")}`}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </span>
              </div>

              <div className={classes.extraFields}>
                <div className={classes.stopsCompDesktop}>
                  <ReactSelectField
                    name="stops"
                    options={getFieldsOptions({ name: "stops", t })}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </div>
                <PaxPicker
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                  adults={search_form.adults}
                  childs={search_form.children}
                  children_ages={search_form.children_ages}
                  // infantsOnLap={search_form.infantsOnLap}
                  // infantsInSeat={search_form.infantsInSeat}
                  onClick={onClickPax}
                  product_type="FL"
                  showLabel={false}
                />
                <DatepickerField
                  name="datesArray"
                  selectsRange={roundTrip}
                  dateFormat="MMM d, yyyy"
                  minDate={DateTime.now().toISODate()}
                  maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                  placeholder={
                    roundTrip ? "Mon DD, YYYY - Mon DD, YYYY" : "Mon DD, YYYY"
                  }
                  withPortal={true}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
                <div className={classes.col2Bottom}>
                  <ReactSelectField
                    name="baggage_only"
                    options={getFieldsOptions({ name: "withBaggage", t })}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                  <ReactSelectField
                    name="flight_class"
                    options={getFieldsOptions({ name: "flightClass", t })}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </div>
              </div>
            </div>
            <div className={classes.searchBtnContr}>
              <SearchButton
                type="submit"
                text={t("search")}
                loading={false}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <PaxPickerModal
          adults={search_form.adults}
          childs={search_form.children}
          children_ages={search_form.children_ages}
          onFormUpdate={({ form: paxData }) => {
            setPax(paxData);
          }}
          onCancel={() => {
            toggleNoScroll();
            setShowPaxPickerModal(false);
          }}
          product_type={"ACC"}
        />
      )}
    </div>
  );
};
DeepFlightSearchForm.propTypes = {};
export default DeepFlightSearchForm;
